import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    makeStyles,
    Paper,
    Tab,
    Tabs,
    Typography,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import DeleteIcon from "@material-ui/icons/Delete";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import TextField from "@material-ui/core/TextField";

const useStyles = makeStyles((theme) => ({
    root: {
        marginTop: theme.spacing(0),
        width: "100%",
    },
    tabPanel: {
        padding: theme.spacing(1),
    },
    chipContainer: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        padding: theme.spacing(2),
    },
    chip: {
        margin: theme.spacing(0.5),
        "& .MuiChip-deleteIcon": {
            color: theme.palette.error.main,
        },
    },
    noDevices: {
        color: theme.palette.text.secondary,
        fontStyle: "italic",
    },
    emptyState: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing(4),
        color: theme.palette.text.secondary,
    },
    tabsContainer: {
        backgroundColor: theme.palette.primary.main,
        "& .MuiTab-root": {
            color: theme.palette.primary.contrastText,
            opacity: 0.7,
            "&.Mui-selected": {
                opacity: 1,
            },
        },
        "& .MuiTabs-indicator": {
            backgroundColor: theme.palette.primary.contrastText,
        },
    },
    deleteButtonContainer: {
        padding: theme.spacing(1),
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: "flex",
        justifyContent: "flex-end",
    },
    cloneButton: {
        color: theme.palette.info.main,
        borderColor: theme.palette.info.main,
        marginRight: theme.spacing(1),
        "&:hover": {
            backgroundColor: theme.palette.info.light,
            borderColor: theme.palette.info.dark,
            color: "white",
        },
    },
    deleteButton: {
        color: theme.palette.error.main,
        borderColor: theme.palette.error.main,
        "&:hover": {
            backgroundColor: theme.palette.error.light,
            borderColor: theme.palette.error.dark,
            color: "white"
        },
    },
    skeletonTabs: {
        display: "flex",
        gap: theme.spacing(1),
        padding: theme.spacing(1),
        backgroundColor: theme.palette.primary.main,
    },
    skeletonTab: {
        borderRadius: 4,
    },
    skeletonChipContainer: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        padding: theme.spacing(2),
    },
    skeletonChip: {
        borderRadius: 16,
    },
}));

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`floor-tabpanel-${index}`}
            aria-labelledby={`floor-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

const GroupDeviceTabs = ({ deviceGroups, onRemoveDevice, onDeleteGroup, onCloneGroup, isLoading }) => {
    const classes = useStyles();
    const [selectedTab, setSelectedTab] = useState(0);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
    const [groupToClone, setGroupToClone] = useState(null);
    const [cloneName, setCloneName] = useState("");

    // Reset selectedTab if it's out of bounds after groups change
    useEffect(() => {
        if (deviceGroups.length > 0 && selectedTab >= deviceGroups.length) {
            setSelectedTab(deviceGroups.length - 1);
        }
    }, [deviceGroups.length, selectedTab]);

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    // Loading skeleton
    if (isLoading) {
        return (
            <Card className={classes.root}>
                <Paper elevation={0}>
                    {/* Skeleton tabs */}
                    <div className={classes.skeletonTabs}>
                        {[1, 2, 3].map((item) => (
                            <Skeleton
                                key={item}
                                variant="rect"
                                width={100}
                                height={36}
                                className={classes.skeletonTab}
                                animation="wave"
                            />
                        ))}
                    </div>
                    {/* Skeleton delete button area */}
                    <div className={classes.deleteButtonContainer}>
                        <Skeleton variant="rect" width={130} height={36} animation="wave" />
                    </div>
                    {/* Skeleton chips */}
                    <div className={classes.skeletonChipContainer}>
                        {[100, 120, 90, 140, 110].map((width, index) => (
                            <Skeleton
                                key={index}
                                variant="rect"
                                width={width}
                                height={32}
                                className={classes.skeletonChip}
                                animation="wave"
                            />
                        ))}
                    </div>
                </Paper>
            </Card>
        );
    }

    // Empty state when no groups exist
    if (!deviceGroups || deviceGroups.length === 0) {
        return (
            <Card className={classes.root}>
                <Paper elevation={0}>
                    <div className={classes.emptyState}>
                        <Typography variant="h6" gutterBottom>
                            Няма създадени групи
                        </Typography>
                        <Typography variant="body2">
                            Създайте нова група от формата вляво
                        </Typography>
                    </div>
                </Paper>
            </Card>
        );
    }

    const handleRemoveDevice = (deviceGroupId, deviceId) => {
        onRemoveDevice(deviceGroupId, deviceId);
    };

    const handleDeleteGroupClick = (group) => {
        setGroupToDelete(group);
        setDeleteDialogOpen(true);
    };

    const handleDeleteGroupConfirm = () => {
        if (groupToDelete) {
            onDeleteGroup(groupToDelete.id);
            setDeleteDialogOpen(false);
            setGroupToDelete(null);
        }
    };

    const handleDeleteGroupCancel = () => {
        setDeleteDialogOpen(false);
        setGroupToDelete(null);
    };

    const handleCloneGroupClick = (group) => {
        setGroupToClone(group);
        setCloneName(`${group.name} (копие)`);
        setCloneDialogOpen(true);
    };

    const handleCloneGroupConfirm = () => {
        if (groupToClone && cloneName.trim()) {
            onCloneGroup(groupToClone, cloneName.trim());
            setCloneDialogOpen(false);
            setGroupToClone(null);
            setCloneName("");
        }
    };

    const handleCloneGroupCancel = () => {
        setCloneDialogOpen(false);
        setGroupToClone(null);
        setCloneName("");
    };

    return (
        <Card className={classes.root}>
            <Paper elevation={0}>
                <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    className={classes.tabsContainer}
                >
                    {deviceGroups.map((deviceGroup, index) => (
                        <Tab
                            key={deviceGroup.id}
                            label={`${deviceGroup.name}`}
                            id={`deviceGroup-tab-${index}`}
                            aria-controls={`deviceGroup-tabpanel-${index}`}
                        />
                    ))}
                </Tabs>

                {deviceGroups.map((deviceGroup, index) => {
                    const devices = deviceGroup.devices || [];

                    return (
                        <TabPanel
                            key={deviceGroup.id}
                            value={selectedTab}
                            index={index}
                            className={classes.tabPanel}
                        >
                            <div className={classes.deleteButtonContainer}>
                                <Button
                                    variant="outlined"
                                    className={classes.cloneButton}
                                    startIcon={<FileCopyIcon />}
                                    onClick={() => handleCloneGroupClick(deviceGroup)}
                                >
                                    Клонирай група
                                </Button>
                                <Button
                                    variant="outlined"
                                    className={classes.deleteButton}
                                    startIcon={<DeleteForeverIcon />}
                                    onClick={() => handleDeleteGroupClick(deviceGroup)}
                                >
                                    Изтрий група
                                </Button>
                            </div>
                            <div className={classes.chipContainer}>
                                {devices.length > 0 ? (
                                    devices.map((device) => (
                                        <Chip
                                            key={device.id}
                                            label={device.name}
                                            onDelete={() =>
                                                handleRemoveDevice(deviceGroup.id, device.id)
                                            }
                                            className={classes.chip}
                                            deleteIcon={<DeleteIcon />}
                                        />
                                    ))
                                ) : (
                                    <Typography className={classes.noDevices}>
                                        Тази група няма зададени устройства!
                                    </Typography>
                                )}
                            </div>
                        </TabPanel>
                    );
                })}
            </Paper>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteGroupCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Потвърждение за изтриване
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Сигурни ли сте, че искате да изтриете групата "{groupToDelete && groupToDelete.name}"? Това ще премахне всички устройства от тази група.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteGroupCancel} color="primary">
                        Отказ
                    </Button>

                    <div className={classes.deleteButtonContainer}>
                        <Button
                        onClick={handleDeleteGroupConfirm}
                        className={classes.deleteButton}
                        variant="outlined"
                        autoFocus
                        >
                            Изтрий
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>

            {/* Clone Group Dialog */}
            <Dialog
                open={cloneDialogOpen}
                onClose={handleCloneGroupCancel}
                aria-labelledby="clone-dialog-title"
            >
                <DialogTitle id="clone-dialog-title">
                    Клониране на група
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Въведете име за новата група. Всички устройства от "{groupToClone && groupToClone.name}" ще бъдат копирани в нея.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Име на новата група"
                        fullWidth
                        variant="outlined"
                        value={cloneName}
                        onChange={(e) => setCloneName(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === "Enter" && cloneName.trim()) {
                                handleCloneGroupConfirm();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloneGroupCancel} color="primary">
                        Отказ
                    </Button>
                    <Button
                        onClick={handleCloneGroupConfirm}
                        color="primary"
                        variant="contained"
                        disabled={!cloneName.trim()}
                    >
                        Клонирай
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default GroupDeviceTabs;
